import { COOKIE_CONSENT_KEY, CookieConsent, CURRENT_POLICY_VERSION, DEFAULT_CONSENT } from '@/config/cookieConfig';
import { supabase } from '@/integrations/supabase/client';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthContext } from './AuthContext';

interface CookieConsentContextType {
  consent: CookieConsent | null;
  showBanner: boolean;
  isLoading: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updatePreferences: (preferences: Partial<Pick<CookieConsent, 'analytics' | 'marketing'>>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  showPreferencesModal: boolean;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

// Helper to update GTM consent
const updateGTMConsent = (analytics: boolean, marketing: boolean) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
    });
  }
};

// Helper to initialize GTM script
const initGTM = (id: string) => {
  if (typeof window === 'undefined' || window.gtmInitialized) return;

  (function (w: Window, d: Document, s: string, l: string, i: string) {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s) as HTMLScriptElement;
    const dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', id);

  window.gtmInitialized = true;
};

// Helper to defer GTM loading based on user context
const deferGTM = (id: string, isInitialConsent: boolean) => {
  if (typeof window === 'undefined' || window.gtmInitialized) return;

  if (isInitialConsent) {
    // For first-time consent, use a 1000ms delay to keep UI smooth
    setTimeout(() => initGTM(id), 1000);
  } else {
    // For returning users, use requestIdleCallback to catch data as safely as possible
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => initGTM(id), { timeout: 2000 });
    } else {
      setTimeout(() => initGTM(id), 500);
    }
  }
};

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAnonymous, loading: authLoading } = useAuthContext();

  const GTM_ID = 'GTM-PLF3KJKN';

  // Save consent to localStorage
  const saveToLocalStorage = useCallback((newConsent: CookieConsent) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent));
    } catch (error) {
      console.error('Failed to save cookie consent to localStorage:', error);
    }
  }, []);

  // Save consent to database for logged-in non-anonymous users
  const saveToDatabase = useCallback(async (newConsent: CookieConsent) => {
    if (!user || isAnonymous) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await supabase
        .from('profiles')
        .update({ cookie_consent: newConsent } as any)
        .eq('id', user.id);

      if (error) {
        console.error('Failed to save cookie consent to database:', error);
      }
    } catch (error) {
      console.error('Failed to save cookie consent to database:', error);
    }
  }, [user, isAnonymous]);

  // Apply consent (update GTM and save)
  const applyConsent = useCallback((newConsent: CookieConsent) => {
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferencesModal(false);

    // Update consent mode first
    updateGTMConsent(newConsent.analytics, newConsent.marketing);

    // Load GTM if consent is given for analytics or marketing
    if (newConsent.analytics || newConsent.marketing) {
      deferGTM(GTM_ID, true);
    }

    saveToLocalStorage(newConsent);
    saveToDatabase(newConsent);
  }, [saveToLocalStorage, saveToDatabase]);

  // Check if consent needs re-prompting due to policy version change
  const needsReprompt = useCallback((existingConsent: CookieConsent): boolean => {
    return existingConsent.policyVersion !== CURRENT_POLICY_VERSION;
  }, []);

  // Load consent on mount
  useEffect(() => {
    const loadConsent = async () => {
      // Wait for auth to be ready
      if (authLoading) return;

      setIsLoading(true);

      // Step 1: Check localStorage first (fast path)
      try {
        const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (storedConsent) {
          const parsed = JSON.parse(storedConsent) as CookieConsent;

          // Check if we need to re-prompt due to policy version change
          if (needsReprompt(parsed)) {
            setShowBanner(true);
            setIsLoading(false);
            return;
          }

          // Valid consent found in localStorage
          setConsent(parsed);
          updateGTMConsent(parsed.analytics, parsed.marketing);

          if (parsed.analytics || parsed.marketing) {
            deferGTM(GTM_ID, false);
          }

          setShowBanner(false);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to parse localStorage consent:', error);
      }

      // Step 2: If logged in (non-anonymous), check database
      if (user && !isAnonymous) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('cookie_consent')
            .eq('id', user.id)
            .single();

          if (!error && data?.cookie_consent) {
            const dbConsent = data.cookie_consent as unknown as CookieConsent;

            // Check if we need to re-prompt due to policy version change
            if (needsReprompt(dbConsent)) {
              setShowBanner(true);
              setIsLoading(false);
              return;
            }

            // Sync to localStorage and apply
            setConsent(dbConsent);
            updateGTMConsent(dbConsent.analytics, dbConsent.marketing);

            if (dbConsent.analytics || dbConsent.marketing) {
              deferGTM(GTM_ID, false);
            }

            saveToLocalStorage(dbConsent);
            setShowBanner(false);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch consent from database:', error);
        }
      }

      // Step 3: No consent found - show banner
      setShowBanner(true);
      setIsLoading(false);
    };

    loadConsent();
  }, [user, isAnonymous, authLoading, needsReprompt, saveToLocalStorage]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent: CookieConsent = {
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
    };
    applyConsent(newConsent);
  }, [applyConsent]);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(() => {
    const newConsent: CookieConsent = {
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
    };
    applyConsent(newConsent);
  }, [applyConsent]);

  // Update specific preferences
  const updatePreferences = useCallback((preferences: Partial<Pick<CookieConsent, 'analytics' | 'marketing'>>) => {
    const newConsent: CookieConsent = {
      analytics: preferences.analytics ?? consent?.analytics ?? DEFAULT_CONSENT.analytics,
      marketing: preferences.marketing ?? consent?.marketing ?? DEFAULT_CONSENT.marketing,
      timestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
    };
    applyConsent(newConsent);
  }, [consent, applyConsent]);

  const openPreferences = useCallback(() => {
    setShowPreferencesModal(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferencesModal(false);
  }, []);

  // Reset consent for testing purposes
  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setConsent(null);
    setShowBanner(true);
  }, []);

  const value: CookieConsentContextType = {
    consent,
    showBanner,
    isLoading,
    acceptAll,
    rejectNonEssential,
    updatePreferences,
    openPreferences,
    closePreferences,
    showPreferencesModal,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Add gtag type to window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    gtmInitialized?: boolean;
  }
}
